// Upload-Submit for ONE asset version — shared by TTSView and STSView.
//
// Both views hand UploadModal an `onSubmit` that: marks the asset busy
// (sidebar spinner), makes sure the cloud-video duration is known, snapshots
// the export input, runs submitAssetUpload, cleans up the partial version on
// a user cancel, records the new version and toasts. The two views differ
// only in the block source (TTS blocks vs. the STS master projection), the
// custom-asset script field, the `isSts` flag and what they reset after a
// successful upload — those come in as parameters.

import { useCallback } from 'react';
import type { Asset, Block as BlockT } from '@shared/types';
import { useSessionStore } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { useToast } from '@/components/ToastProvider';
import { cancelAbortedUpload } from '@/lib/cancelUpload';
import { snapshotExportInput } from '@/lib/exportSnapshot';
import { ensureVideoDurationFor } from '@/lib/probeVideoDuration';
import { submitAssetUpload, type UploadTarget } from '@/lib/uploadPipeline';
import { buildAssetFolderName, fileLangCode } from '@/lib/uploadFilenames';
import { assetGlobalIdx } from '@/lib/assetNumber';

type ViewAsset = Asset & { _isCustom?: boolean };
type UploadResult = Awaited<ReturnType<typeof submitAssetUpload>>;

export interface UploadSubmitArgs {
  note: string | null;
  includeVideo: boolean;
  onProgress: NonNullable<Parameters<typeof submitAssetUpload>[0]['onProgress']>;
  signal: AbortSignal;
}

interface UseAssetUploadSubmitOptions {
  activeAsset: ViewAsset | undefined;
  currentAssetIdx: number;
  isCustom: boolean;
  /** Per-mode script field of custom assets (`scriptTts` in TTS mode,
   *  `scriptSts` in STS mode); predefined assets use `script` verbatim. */
  customScriptField: 'scriptTts' | 'scriptSts';
  voiceName: string;
  blocks: BlockT[];
  isSts: boolean;
  nextVersion: number;
  /** Bytes des Cloud-Videos (0 = keins, null = unbekannt). */
  cloudVideoSize: number | null;
  /** Prefix for the cancel-cleanup log line. */
  logTag: string;
  /** View-specific reset after a successful upload (runs after
   *  `addVersion`, before the success toast). */
  onUploaded: () => void;
}

export function useAssetUploadSubmit({
  activeAsset,
  currentAssetIdx,
  isCustom,
  customScriptField,
  voiceName,
  blocks,
  isSts,
  nextVersion,
  cloudVideoSize,
  logTag,
  onUploaded,
}: UseAssetUploadSubmitOptions) {
  // Slices subscribed individually so the handler closure picks up the
  // latest values without forcing unrelated re-renders.
  const toolId = useSessionStore((s) => s.toolId);
  const config = useSessionStore((s) => s.config);
  const sessionFolder = useSessionStore((s) => s.sessionFolder);
  const shortId = useSessionStore((s) => s.shortId);
  const userName = useSessionStore((s) => s.name);
  const freeMode = useSessionStore((s) => s.freeMode);
  const addVersion = useSessionStore((s) => s.addVersion);
  const videoBlobs = useVideoStore((s) => s.blobs);
  const toast = useToast();
  // langCode prefix: prefer the explicit admin field; fall back to the
  // first 3 chars of the project name. `'TRO'` is a defensive last resort
  // so filenames never end up starting with `_`.
  const langCode = fileLangCode(config);

  return useCallback(
    async ({ note, includeVideo, onProgress, signal }: UploadSubmitArgs) => {
      if (!toolId || !activeAsset || !sessionFolder) {
        throw new Error('Session not ready for upload');
      }
      // Sidebar/pill-bar spinner — mirrors vanilla `_sidebarSpinners.add` /
      // `_sidebarSpinners.clear` (legacy/index.html lines 3729 + 3801).
      // try/finally so a thrown upload error still clears the spinner.
      useSessionStore.getState().setAssetBusy(currentAssetIdx, true);
      try {
        // Cloud-Video-Dauer sicherstellen, bevor der Snapshot die Länge festlegt (Audit H6).
        await ensureVideoDurationFor(toolId, currentAssetIdx, activeAsset);
        // Build the ExportInput the same way useExport.exportVoiceOnly does —
        // shared helper reads stores at the moment the run starts so a
        // mid-upload mixer/store edit can't taint the rendered WAV.
        const { exportInput, videoSrc: snapshotVideoSrc } = snapshotExportInput(currentAssetIdx);
        let result: UploadResult;
        // Tatsächliche Version/Ordner aus der Pipeline — der Cancel räumt
        // genau diese auf, nicht die lokale nextVersion (Audit M15).
        let uploadTarget: UploadTarget = {
          version: nextVersion,
          assetFolderName: buildAssetFolderName({
            assetIdx: assetGlobalIdx(activeAsset, currentAssetIdx),
            title: activeAsset.title,
            isCustom,
          }),
        };
        try {
          result = await submitAssetUpload({
            toolId,
            videoSrc: snapshotVideoSrc,
            sessionFolder,
            langCode,
            shortId,
            userName,
            // Globale Asset-Nummer für Ordner/Dateinamen (Audit 2026-09-24 H4).
            assetIdx: assetGlobalIdx(activeAsset, currentAssetIdx),
            asset: {
              id: activeAsset.id,
              videoPath: activeAsset.videoPath ?? null,
              title: activeAsset.title,
              assetType: activeAsset.assetType,
              // Custom assets use the per-mode script (scriptTts in TTS-mode,
              // scriptSts in STS-mode); predefined assets keep the admin-set
              // `script` verbatim. The legacy `script` field is the
              // silent-migration fallback.
              script: isCustom
                ? activeAsset[customScriptField] ?? activeAsset.script ?? ''
                : activeAsset.script ?? '',
              isCustom,
              // Multi-Voice (#173) — pipeline appends _VoiceN suffix when
              // length ≥ 2; single-voice assets keep their pre-Phase-9
              // filenames byte-identical.
              voiceIds: activeAsset.voiceIds ?? [],
            },
            voiceName,
            blocks,
            videoDuration: useVideoStore.getState().durations[currentAssetIdx] ?? 0,
            exportInput,
            // Free/custom assets forward their sub-mode, which the server
            // persists as `version.mode` so VersionsBar can render the
            // TTS/STS chip on custom-asset pills (FB#12). Predefined
            // TTS-only / STS-only assets pass undefined → server omits
            // `mode` (the chip is redundant when the asset type is fixed).
            // Free-mode rows only ever store 'tts' | 'sts'; the wider
            // AssetType union exists because freeMode shares its value type
            // with `assetType`.
            freeMode:
              activeAsset.assetType === 'free'
                ? (freeMode[currentAssetIdx] as 'tts' | 'sts' | undefined)
                : undefined,
            nextVersion,
            includeVideo,
            videoBlob: includeVideo ? videoBlobs[currentAssetIdx] ?? null : null,
            // Bytes des Cloud-Videos (0 = keins, null = unbekannt). Die Pipeline
            // entscheidet damit über das automatische Premix-MP4.
            cloudVideoSize,
            note,
            isSts,
            onProgress,
            signal,
            onUploadTarget: (t) => { uploadTarget = t; },
          });
        } catch (err) {
          // User-initiated cancel: wipe the partial WebDAV folder + any KV
          // version record before propagating AbortError up to the modal.
          // Cleanup endpoint is idempotent (a 404 from WebDAV is treated as
          // success).
          if ((err as { name?: string } | null)?.name === 'AbortError') {
            // 409 version_complete → Versionen nachladen (lib/cancelUpload.ts).
            await cancelAbortedUpload(toolId, {
              sessionFolder,
              assetFolderName: uploadTarget.assetFolderName,
              version: uploadTarget.version,
              uploadId: uploadTarget.uploadId,
              assetId: activeAsset.id,
            }, logTag);
          }
          throw err;
        }
        // Append to versions store so the asset summary reflects the new run.
        addVersion(activeAsset.id, result);
        onUploaded();
        toast.show(`Uploaded version ${result.version}`, 3000);
      } finally {
        useSessionStore.getState().setAssetBusy(currentAssetIdx, false);
      }
    },
    [
      toolId,
      activeAsset,
      sessionFolder,
      langCode,
      shortId,
      userName,
      currentAssetIdx,
      isCustom,
      customScriptField,
      voiceName,
      blocks,
      isSts,
      freeMode,
      nextVersion,
      videoBlobs,
      cloudVideoSize,
      addVersion,
      logTag,
      onUploaded,
      toast,
    ],
  );
}
